import { LogOut } from 'lucide-react';

export type AppShellTopBarUser = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
} | null;

type AppShellTopBarAccountVariant = 'topBar' | 'sidebar';

/**
 * Account chip: default `topBar` for headers that still show it; `sidebar` for bottom of app shell sidebars (IMS, VMS).
 */
export function AppShellTopBarAccount({
  user,
  onLogoutClick,
  variant = 'topBar',
}: {
  user: AppShellTopBarUser;
  onLogoutClick: () => void;
  variant?: AppShellTopBarAccountVariant;
}) {
  const chip = (
    <div
      className={
        variant === 'sidebar'
          ? 'flex w-full min-w-0 items-center gap-2 rounded-xl border border-gray-200/90 bg-white/90 py-2 pl-2 pr-1.5 shadow-sm'
          : 'flex items-center gap-2 sm:gap-3 rounded-2xl border border-gray-200/90 bg-gradient-to-br from-gray-50/90 to-white py-1.5 pl-2 pr-1.5 shadow-sm'
      }
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="app-shell-sidebar-avatar app-shell-sidebar-avatar--sm shrink-0 shadow-md ring-2 ring-white">
          <span aria-hidden="true">
            {user?.first_name?.[0]}
            {user?.last_name?.[0]}
          </span>
        </div>
        <div className={`min-w-0 flex-1 text-left ${variant === 'topBar' ? 'hidden sm:block' : ''}`}>
          <div className="truncate text-sm font-semibold leading-tight text-gray-900">
            {user?.first_name} {user?.last_name}
          </div>
          <div className="truncate text-xs text-gray-500">{user?.email}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onLogoutClick}
        className="app-shell-account-btn shrink-0 rounded-xl p-2 text-gray-500 transition-colors hover:text-primary-700"
        aria-label={`Logout from account. Currently logged in as ${user?.first_name} ${user?.last_name}`}
        title="Logout"
      >
        <LogOut className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );

  if (variant === 'sidebar') {
    return <div className="w-full min-w-0">{chip}</div>;
  }

  return <div className="flex w-full shrink-0 items-center justify-end sm:w-auto">{chip}</div>;
}
